import { type FunctionRec, type RequestMetadata } from './auditLogTypes';

/** Exact FF_ROLE requests should not expose video-related controls. */
export function isFfRoleRequestType(requestType: string | undefined): boolean {
  return requestType === 'FF_ROLE';
}

/** Firefighter role-only requests have no entitlement list; hide the row in request details. */
export function shouldShowEntitlementsRequestedRow(
  requestType: string | undefined
): boolean {
  return !isFfRoleRequestType(requestType);
}

/** FF_ROLE requests have no session recording control in the audit log list toolbar. */
export function shouldShowAuditLogRecordingButton(
  requestType: string | undefined
): boolean {
  return !isFfRoleRequestType(requestType);
}

/** Endpoint / context label when `endpointDisplayName` is absent (e.g. FF_ROLE). */
export function resolveAuditLogEndpointDisplayName(
  m: RequestMetadata | null
): string {
  if (!m) {
    return '';
  }

  const first = [
    m.endpointDisplayName,
    m.requestHeaderDisplayName,
    m.ffRoleName,
  ]
    .map((raw) => raw.trim())
    .find((t) => t.length > 0);

  return first ?? '';
}

/**
 * `endpoint` for `/api/v5/getEntitlements` — uses `requestDetail.endpointName` for
 * FF_ID; empty for exact FF_ROLE (no endpoint on role-only requests).
 */
export function resolveAuditLogEntitlementsEndpoint(
  m: RequestMetadata | null
): string {
  if (!m || isFfRoleRequestType(m.requestType)) {
    return '';
  }

  return (m.endpointName ?? '').trim();
}

/** Used when `requestReviewDetails` omits or sends invalid `maxDateRangeMonths`. */
export const DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS = 6;

/**
 * Reads `data.extraAttribute.exportConstraint.maxDateRangeMonths` from the
 * request review details API. Non-finite, non-positive, or non-integer values
 * fall back to {@link DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS}.
 */
export function parseAuditExportMaxDateRangeMonths(
  data: { extraAttribute?: unknown } | null | undefined
): number {
  if (!data || typeof data !== 'object') {
    return DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS;
  }

  const extra = data.extraAttribute;

  if (!extra || typeof extra !== 'object') {
    return DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS;
  }

  const constraint = (extra as { exportConstraint?: unknown }).exportConstraint;

  if (!constraint || typeof constraint !== 'object') {
    return DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS;
  }

  const raw = (constraint as { maxDateRangeMonths?: unknown })
    .maxDateRangeMonths;
  const n = typeof raw === 'number' ? raw : Number(raw);

  if (Number.isFinite(n) && n > 0 && Number.isInteger(n)) {
    return n;
  }

  return DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS;
}

const mapUniqueFunctions = (
  rawData: FunctionRec[]
): { functionId: number; functionName: string }[] => {
  const seen = new Set<string>();

  return rawData
    .map((rec) => {
      const fn = typeof rec.functionName === 'string' ? rec.functionName : '';

      if (!fn || seen.has(fn)) {
        return null;
      }

      seen.add(fn);

      const idRaw = rec.functionKey ?? rec.id;
      const idNum = typeof idRaw === 'number' ? idRaw : Number(idRaw);

      return {
        functionId: Number.isFinite(idNum) ? idNum : 0,
        functionName: fn,
      };
    })
    .filter((v): v is { functionId: number; functionName: string } =>
      Boolean(v?.functionName)
    );
};

export default mapUniqueFunctions;
